import {
  EMPTY_RAIL_CATEGORY_ORDER,
  EMPTY_RAIL_PIN_STORE,
} from "@/components/navigation/project-nav-rail-pins";
import {
  migrateSidebarRowLayout,
  migrateSidebarSectionOrder,
  useSidebarSectionOrders,
  useStoredSidebarRowLayout,
} from "@/components/navigation/project-nav-sidebar-layout";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useCallback } from "react";

/** Marks a file as a nav setup, so an unrelated JSON file is refused rather than half-applied. */
export const NAV_SETUP_FORMAT = "chalk-nav-setup";
/** Raised when a setup's shape changes in a way older readers cannot take. */
export const NAV_SETUP_VERSION = 1;

/**
 * Everything a reader arranges in the category nav, as its stores hold it: the rail's icon order
 * and pins, and each sidebar's section order and row layout. Each part is read as defensively as
 * the store it came from, so a setup from a slightly different nav still applies.
 */
export interface NavSetup {
  railCategoryOrder: Record<string, unknown>;
  railPins: Record<string, unknown>;
  sidebarSectionOrder: Record<string, unknown>;
  sidebarRowLayout: Record<string, unknown>;
}

const NAV_SETUP_PARTS = [
  "railCategoryOrder",
  "railPins",
  "sidebarSectionOrder",
  "sidebarRowLayout",
] as const satisfies readonly (keyof NavSetup)[];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object" && !Array.isArray(value);

/** A setup as the portable file a reader shares. */
export const serializeNavSetup = (setup: NavSetup, exportedAt: Date): string =>
  JSON.stringify(
    {
      format: NAV_SETUP_FORMAT,
      version: NAV_SETUP_VERSION,
      exportedAt: exportedAt.toISOString(),
      ...setup,
    },
    null,
    2,
  );

export type NavSetupParseResult = { ok: true; setup: NavSetup } | { ok: false; error: string };

/**
 * A shared setup file's contents, or why it can't be imported. A part the file leaves out, or holds
 * in the wrong shape, reads as that store's default, so importing replaces the whole setup.
 */
export const parseNavSetup = (text: string): NavSetupParseResult => {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { ok: false, error: "That file isn't valid JSON." };
  }
  if (!isRecord(value) || value.format !== NAV_SETUP_FORMAT) {
    return { ok: false, error: "That file isn't a Chalk nav setup." };
  }
  if (typeof value.version !== "number" || value.version > NAV_SETUP_VERSION) {
    return { ok: false, error: "That nav setup comes from a newer version of Chalk." };
  }
  const setup = Object.fromEntries(
    NAV_SETUP_PARTS.map((part) => [part, isRecord(value[part]) ? value[part] : {}]),
  ) as unknown as NavSetup;
  return { ok: true, setup };
};

/** The reader's whole nav setup, and commands to export it as a file and to replace it with one. */
export const useNavSetupTransfer = () => {
  const [railCategoryOrder, setRailCategoryOrder] = useLocalStorageSyncedState(
    LocalStorageKey.PROJECT_NAV_RAIL_CATEGORY_ORDER,
    EMPTY_RAIL_CATEGORY_ORDER,
  );
  const [railPins, setRailPins] = useLocalStorageSyncedState(
    LocalStorageKey.PROJECT_NAV_RAIL_PINS,
    EMPTY_RAIL_PIN_STORE,
  );
  const { sectionOrders: sidebarSectionOrder, setSectionOrders: setSidebarSectionOrder } =
    useSidebarSectionOrders();
  const [sidebarRowLayout, setSidebarRowLayout] = useStoredSidebarRowLayout();

  /** Downloads the setup as `chalk-nav-setup-<date>.json`. */
  const exportSetup = useCallback(() => {
    const now = new Date();
    const blob = new Blob(
      [
        serializeNavSetup(
          { railCategoryOrder, railPins, sidebarSectionOrder, sidebarRowLayout },
          now,
        ),
      ],
      { type: "application/json" },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `chalk-nav-setup-${now.toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [railCategoryOrder, railPins, sidebarRowLayout, sidebarSectionOrder]);

  /** Replaces the reader's setup with an imported one. */
  const importSetup = useCallback(
    (setup: NavSetup) => {
      setRailCategoryOrder({ ...EMPTY_RAIL_CATEGORY_ORDER, ...setup.railCategoryOrder });
      setRailPins({ ...EMPTY_RAIL_PIN_STORE, ...setup.railPins });
      // A setup from before a change to the default sidebars is migrated like a stored one.
      setSidebarSectionOrder(migrateSidebarSectionOrder(setup.sidebarSectionOrder));
      setSidebarRowLayout(migrateSidebarRowLayout(setup.sidebarRowLayout));
    },
    [setRailCategoryOrder, setRailPins, setSidebarRowLayout, setSidebarSectionOrder],
  );

  return { exportSetup, importSetup };
};
